"use client";

import Input from "@/components/atom/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getAllStock, getBrand, getCategory } from "@/functions/databaseData";
import useHeader from "@/functions/useHeader";
import { cn } from "@/lib/utils";
import {
  CheckboxCheckedSvg,
  CheckboxUncheckSvg,
  RefreshSvg,
  SearchSvg,
} from "@/public/svg";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FooterTable } from "../member-area/_component";
import React, { useEffect, useMemo, useState } from "react";
import Loading from "@/components/atom/Loading";
import Crumbs from "@/components/atom/Crumbs";
import Link from "next/link";
import { useDebounce } from "use-debounce";
import { ChevronRight } from "lucide-react";
import Container from "@/components/atom/Container";
import { useCheckBreakPoint } from "@/functions/usResponsive";
import Divider from "@/components/atom/Divider";
import isEmpty from "lodash/isEmpty";
import useDelayedRender from "@/functions/useDelayedRender";

const sizeOrder = {
  XXS: 1,
  XS: 2,
  S: 3,
  M: 4,
  L: 5,
  XL: 6,
  XXL: 7,
  "3L": 8,
  "4L": 9,
  11: 10,
  "ALL SIZE": 0,
  3: 35,
  2: 34,
  1: 33,
  0: 32,
  L14: 31,
  "P0+": 30,
  "L0+": 29,
  XXXXXXXL: 28,
  XXXXXXL: 27,
  P11: 26,
  P9: 25,
  P7: 24,
  P5: 23,
  P3: 22,
  P1: 21,
  P0: 20,
  L12: 19,
  L10: 18,
  L8: 17,
  L6: 16,
  L4: 15,
  L2: 14,
  L0: 13,
  "UMUR 3-5": 12,
  "UMUR 1-3": 11,
  4: 36,
  5: 37,
  6: 38,
  7: 39,
  8: 40,
  9: 41,
  10: 42,
  XXXL: 43,
  "UMUR 5-7": 44,
  "UKURAN 130": 45,
  "UKURAN 150": 46,
  "4gr": 47,
  "5gr": 48,
  35: 85,
  36: 86,
  37: 87,
  38: 88,
  39: 89,
  40: 90,
  41: 91,
  42: 92,
  43: 93,
  44: 94,
  45: 95,
  46: 96,
  "7L": 97,
  "11L": 98,
  "9L": 99,
  "280gr": 49,
  "30ML": 50,
  "60ML": 51,
  27: 52,
  28: 53,
  29: 54,
  A5: 55,
  B5: 56,
  "8 GR": 57,
  "20ML": 58,
  "12gr": 59,
  "15ML": 60,
  30: 61,
  31: 62,
  32: 63,
  33: 64,
  34: 65,
  "3XL": 66,
  "4XL": 67,
  "5XL": 68,
  A1: 10,
  G3: 69,
  G5: 70,
  G7: 71,
  G9: 72,
  G11: 73,
  B4: 74,
  B6: 75,
  B8: 76,
  B10: 77,
  B12: 78,
  A2: 12,
  "7XL": 99,
  A3: 99,
};

export default function TableStock() {
  const shouldRendered = useDelayedRender(100);
  const shouldRendered1 = useDelayedRender(300);

  let breakpoint = useCheckBreakPoint();
  const { session } = useHeader();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(900);
  const [loading, setLoading] = useState({});
  const [forms, setForms] = useState({});
  const [showDropdown, setShowDropdown] = useState({});
  const [debounceSearch] = useDebounce(search, 700);
  const queryClient = useQueryClient();

  const {
    data: datas,
    isLoading: loadingFirstGet,
    refetch,
  } = useQuery({
    queryKey: ["list-all-stock"],
    queryFn: async () => {
      let data = await getAllStock({
        token: session?.user?.token,
        limit: breakpoint == "ui-phone" ? 1000 : 1500,
      });
      setLoading((old) => ({ ...old, reset: false }));
      if (!data) return [];
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const { data: brands } = useQuery({
    queryKey: ["listbrand"],
    queryFn: async () => {
      const res = await getBrand();
      return res ?? [];
    },
    refetchOnWindowFocus: false,
  });

  const { data: categories } = useQuery({
    queryKey: ["listcategorie"],
    queryFn: async () => {
      const res = await getCategory({
        by: "all",
      });
      return res ?? [];
    },
    refetchOnWindowFocus: false,
  });

  async function getDataSearch() {
    try {
      let data = await getAllStock({
        token: session?.user?.token,
        search,
        limit: breakpoint == "ui-phone" ? 1000 : 1500,
      });
      queryClient.setQueryData(["list-all-stock"], () => data);
    } catch (error) {
      console.log(error?.message);
    } finally {
      setLoading((old) => ({ ...old, search: false }));
    }
  }

  useEffect(() => {
    if (!loadingFirstGet && debounceSearch?.length > 0) getDataSearch();
    else {
      refetch();
      setLoading((old) => ({ ...old, search: false }));
    }
  }, [debounceSearch]);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;

    let isListOrder = datas
      ?.sort(
        (a, b) =>
          a?.name_product.localeCompare(b?.name_product) ||
          a?.variant2?.localeCompare(b?.variant2) ||
          (sizeOrder[a.variant1] !== undefined ? sizeOrder[a.variant1] : 99) -
            (sizeOrder[b.variant1] !== undefined ? sizeOrder[b.variant1] : 99)
      )
      ?.filter((item) => {
        return search?.length > 0
          ? item?.variant_id?.toLowerCase()?.includes(search?.toLowerCase()) ||
              item?.sku?.toLowerCase()?.includes(search?.toLowerCase()) ||
              item?.name_product
                ?.toLowerCase()
                ?.includes(search?.toLowerCase()) ||
              item?.diskon_product
                ?.toLocaleString()
                ?.toLowerCase()
                ?.includes(search?.toLowerCase())
          : item;
      });

    isListOrder = isListOrder;

    return isListOrder?.slice(firstPageIndex, lastPageIndex);
  }, [pageSize, currentPage, search, datas]);

  const groupProducts = (data) => {
    const grouped = {};

    data.forEach((item) => {
      if (!grouped[item.product_id]) {
        grouped[item.product_id] = {
          ...item,
          variants: {},
        };
      }
      if (!grouped[item.product_id].variants[item.variant2]) {
        grouped[item.product_id].variants[item.variant2] = [];
      }
      grouped[item.product_id].variants[item.variant2].push({
        variant_id: item.variant_id,
        stock: item.stock,
        price_tags: item.price_tags,
        launching: item.launching,
        diskon_product: item.diskon_product,
        variant1: item.variant1,
      });
    });

    return Object.values(grouped);
  };

  const products = groupProducts(currentTableData || []);
  const listFilter = (type) => {
    switch (type) {
      case "brand":
        return brands?.map((a) => a?.name_brand);
        break;
      case "category":
        return categories?.map((a) => a?.name_category);
        break;
      case "discount":
        return [5, 10, 15, 20, 25, 30];
        break;
      default:
        break;
    }
  };

  async function runFilterProduct({ key, val }) {
    setLoading((old) => ({ ...old, filter: true }));
    try {
      let data = await getAllStock({
        token: session?.user?.token,
        limit: breakpoint == "ui-phone" ? 1000 : 1500,
        brand: forms?.brand,
        category: forms?.category,
        discount: forms?.discount?.replaceAll("%", ""),
        type_product: forms?.type_product,
        [key]: val,
      });
      if (!data) return;
      queryClient.setQueryData(["list-all-stock"], () => data);
    } catch (error) {
      console.log(error?.message);
    } finally {
      setLoading((old) => ({ ...old, filter: false }));
    }
  }

  return (
    <Container
      showMQ={false}
      className={"flex gap-5 xs:gap-8 flex-col z-10"}>
      <div
        className={cn(
          "mt-16 xs:mt-32 container-mini place-items-center w-full "
        )}>
        <Crumbs />
        {shouldRendered && (
          <div className='flex md:flex-row flex-col gap-4 xs:sticky xs:top-0 top-[2.5em] bg-shadesGON-50 z-10 mb-4'>
            <Input
              icon={{
                left: (props) => <SearchSvg {...props} />,
              }}
              placeholder={`Cari nama, warna atau sku produk`}
              onChange={(val) => {
                setLoading((old) => ({ ...old, search: true }));
                setSearch(val);
              }}
              value={search}
              onSubmit={(e) => e.preventDefault()}
              enterKeyHint={"search"}
            />
            {["brand", "category", "discount"]?.map((a, b) => {
              let isZindex = 3 - b;
              let disableInput = !session?.user?.token && a == "discount";
              return (
                <div
                  key={b}
                  className={cn(`z-[${isZindex}]`, "flex w-full")}>
                  <div className='flex w-full flex-col gap-2'>
                    <Input
                      value={forms?.[a]?.length > 0 ? forms?.[a] : ""}
                      placeholder={`Filter by ${a} ${
                        disableInput ? "- butuh Login" : ""
                      }`}
                      disableInput={disableInput}
                      disabled={disableInput}
                      onChange={(val) => {
                        setForms((old) => ({
                          ...old,
                          [a]: val,
                        }));
                      }}
                      variants='dropdown'
                      handleDropdown={{
                        show: showDropdown[a],
                        showHIdden: () => {
                          setShowDropdown((old) => ({
                            [a]: !showDropdown?.[a],
                          }));
                        },
                        datas: listFilter(a)?.map((val, idx) => ({
                          title: a == "discount" ? `${val}%` : val,
                          val,
                          id: idx,
                        })),
                        onChange: (value) => {
                          setForms((old) => ({
                            ...old,
                            [a]: value?.title,
                          }));
                          runFilterProduct({
                            key: a,
                            val: value?.val,
                          });
                        },
                        value: forms?.[a],
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {shouldRendered1 && (
          <>
            <div className='flex flex-row justify-between gap-4 mb-4'>
              <div className='flex flex-row gap-4'>
                {["sale", "regular"]?.map((a, b) => {
                  return !session?.user?.token && a == "sale" ? null : (
                    <div
                      key={b}
                      className='flex gap-2 items-center cursor-pointer w-fit'
                      onClick={() => {
                        let oldVal = forms?.type_product;
                        setForms((old) => ({
                          ...old,
                          type_product: oldVal == a ? "" : a,
                        }));
                        runFilterProduct({
                          key: "type_product",
                          val: oldVal == a ? "" : a,
                        });
                      }}>
                      {forms?.type_product == a ? (
                        <CheckboxCheckedSvg className='h-icon-small w-icon-small text-primaryGON-400' />
                      ) : (
                        <CheckboxUncheckSvg className='h-icon-small w-icon-small text-greyscaleGON-500' />
                      )}
                      <p className='p3-regular text-greyscaleGON-600 capitalize'>
                        {a}
                      </p>
                    </div>
                  );
                })}
              </div>
              {(!isEmpty(forms) || loading?.filter || loading?.reset) && (
                <div
                  onClick={() => {
                    if (loading?.filter || loading?.reset) return;
                    setForms({});
                    setLoading((old) => ({ ...old, reset: true }));
                    refetch();
                  }}
                  className='p3-regular text-primaryGON-400 cursor-pointer hover:underline hover:underline-offset-1'>
                  {loading?.filter || loading?.reset ? (
                    <RefreshSvg className='text-primaryGON-200 animate-spin w-4 h-4' />
                  ) : (
                    "reset filter"
                  )}
                </div>
              )}
            </div>
            {breakpoint == "ui-tablet" || breakpoint == "ui-phone" ? (
              <TableVersiMobile
                products={products ?? []}
                loading={loading}
                loadingFirstGet={loadingFirstGet}
              />
            ) : (
              <TableVersiLaptop
                products={products ?? []}
                loading={loading}
                loadingFirstGet={loadingFirstGet}
                currentPage={currentPage}
                currentTableData={currentTableData}
                datas={datas}
                setCurrentPage={setCurrentPage}
                search={search}
                pageSize={pageSize}
                setPageSize={setPageSize}
              />
            )}
          </>
        )}
      </div>
    </Container>
  );
}

const TableVersiLaptop = ({
  products,
  loading,
  loadingFirstGet,
  currentPage,
  currentTableData,
  datas,
  setCurrentPage,
  search,
  pageSize,
  setPageSize,
}) => {
  const { session } = useHeader();
  return (
    <>
      <Table
        className={cn(
          "border border-greyscaleGON-200 relative group",
          loading?.filter || loading?.reset
            ? "opacity-50 pointer-events-none"
            : ""
        )}>
        <TableHeader className='sticky top-0 xs:top-[3em] z-[8] bg-shadesGON-50'>
          <TableRow>
            <TableHead className='text-left w-[10px]'>No</TableHead>
            <TableHead className='text-center w-[100px]'>Warna</TableHead>
            <TableHead className='text-right w-[100px]'>Ukuran</TableHead>
            <TableHead className='text-right w-[100px]'>Total Stok</TableHead>
            <TableHead className='text-right w-[100px]'>
              Diskon Tambahan
            </TableHead>
            {(!session?.user?.token) || (session?.user?.data?.mitra_level !== 1) ? null : (
            <TableHead className='text-right w-[100px]'>
              Usia (Hari)
            </TableHead>
            )}
            <TableHead className='text-right w-[100px]'>Aksi</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className='border border-greyscaleGON-200'>
          {products?.map((product, idx) => {
            const totalStock = Object.values(product.variants)
              .flat()
              .reduce((acc, variant) => acc + variant.stock, 0);
            const totalDiskon = Object.values(product.variants)
              .flat()
              .reduce(
                (acc, variant) => acc + parseInt(variant.diskon_product),
                0
              );

            const firstRowSpan = currentTableData?.filter(
              (a) => a?.product_id == product?.product_id
            )?.length;

            const dataUsia = product?.launching?.split(',')
            const usia = `${dataUsia?.[0]} tahun, ${dataUsia?.[1]} bulan, ${dataUsia?.[2]} hari`
            return (
              <React.Fragment key={product.product_id}>
                <TableRow className='z-10 group/btn  border border-greyscaleGON-200'>
                  <TableCell
                    rowSpan={firstRowSpan + 1}
                    className=' justify-start text-center relative bg-primaryGON-50'>
                    <div>{idx + 1}</div>
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    className='text-center sticky top-0 xs:top-[6em] bg-shadesGON-50 '>
                    <span className='p3-semiBold text-secondaryGON-400'>
                      {product?.name_product}
                    </span>
                  </TableCell>
                  {/* <TableCell className="text-right"></TableCell> */}
                  <TableCell className='text-right sticky top-0 xs:top-[6em] bg-secondaryGON-100 '>
                    <span className='p3-semiBold text-secondaryGON-400'>
                      {totalStock}
                    </span>
                  </TableCell>
                  <TableCell className='text-right sticky top-0 xs:top-[6em] bg-shadesGON-50 '>
                    {/* {totalDiskon}% */}
                  </TableCell>
                  {(!session?.user?.token) || (session?.user?.data?.mitra_level !== 1) ? null : (
                  <TableCell className='text-right text-wrap sticky top-0 xs:top-[6em] bg-shadesGON-50 '>
                    {!product?.launching ? '-' : usia}
                  </TableCell>
                  )}
                  <TableCell className='text-right sticky top-0 xs:top-[6em] bg-shadesGON-50 '>
                    <Link
                      aria-label={`${product?.name_product}`}
                      as={`/products/${product?.name_product
                        ?.replaceAll("&", "and")
                        ?.replaceAll(" ", "-")}-${product?.product_id}`}
                      shallow
                      passHref
                      target='_blank'
                      rel='preload'
                      href={`/products/${product?.name_product
                        ?.replaceAll("&", "and")
                        ?.replaceAll(" ", "-")}-${product?.product_id}`}
                      className='flex justify-end items-center'>
                      <div className='flex items-center gap-1 overflow-hidden'>
                        <span
                          className={cn(
                            "whitespace-nowrap truncate p3-semiBold text-primaryGON-400"
                          )}>
                          Beli Sekarang
                        </span>
                        <ChevronRight className='group-hover/btn:flex translate-x-10 w-4 h-4 text-primaryGON-400 group-hover/btn:translate-x-0 duration-500' />
                      </div>
                    </Link>
                  </TableCell>
                </TableRow>
                {Object.keys(product.variants).map((color, index) => (
                  <React.Fragment key={color}>
                    {product.variants[color].map((variant, varIndex) => (
                      <TableRow
                        key={variant.variant_id}
                        className='items-start justify-start border border-greyscaleGON-200'>
                        {/* {varIndex === 0 && (
                      )} */}
                        <TableCell
                          // rowSpan={product.variants[color].length}
                          className='text-center '>
                          {color}
                        </TableCell>
                        <TableCell className='text-right'>
                          {variant.variant1}
                        </TableCell>
                        <TableCell className='text-right'>
                          {variant.stock}
                        </TableCell>
                        <TableCell className='text-right'>
                          {variant.diskon_product}%
                        </TableCell>
                        <TableCell className='text-right'></TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
        {(loadingFirstGet || loading?.search) && (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={7}>Loading...</TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
      <FooterTable
        currentPage={currentPage}
        totalCount={
          datas?.filter((item) => {
            return search?.length > 0
              ? item?.variant_id
                  ?.toLowerCase()
                  ?.includes(search?.toLowerCase()) ||
                  item?.sku?.toLowerCase()?.includes(search?.toLowerCase()) ||
                  item?.name_product
                    ?.toLowerCase()
                    ?.includes(search?.toLowerCase())
              : item;
          })?.length
        }
        pageSize={pageSize}
        setPageSize={setPageSize}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </>
  );
};

const TableVersiMobile = ({ products, loading, loadingFirstGet }) => {
  let breakpoint = useCheckBreakPoint();
  const {session} = useHeader()

  return (
    <div className='flex flex-col gap-2 mt-2 mb-10'>
      <>
        {loadingFirstGet || loading?.search ? (
          <p className='text-xs font-poppins text-greyscaleGON-400'>
            loading...
          </p>
        ) : (
          products?.map((val, idx) => {
            const totalStock = Object.values(val.variants)
              .flat()
              .reduce((acc, variant) => acc + variant.stock, 0);


            const dataUsia = val?.launching?.split(',')
            const usia = `${dataUsia?.[0]} tahun, ${dataUsia?.[1]} bulan, ${dataUsia?.[2]} hari`
            return (
              <div
                key={idx}
                className={cn(
                  "xs:p-4 p-2 bg-shadesGON-50 border border-greyscaleGON-200 rounded flex flex-col gap-2 ",
                  loading?.filter || loading?.reset
                    ? "opacity-50 pointer-events-none"
                    : ""
                )}>
                <div className='sticky xs:top-[2.5em] top-[2.5em] py-2 bg-shadesGON-50'>
                  <p className='subheading-regular'>{val?.name_product}</p>
                </div>
                {(!session?.user?.token) || (session?.user?.data?.mitra_level !== 1) ? null : (
                  <div className='py-2 bg-shadesGON-50'>
                    <p className='p3-regular text-wrap '>Usia (Hari) : {!val?.launching ? '-' :usia}</p>
                  </div>
                )}
                <div className='flex flex-col gap-1'>
                  <div className='flex justify-between items-center gap-1'>
                    <div className='p3-regular p-2 bg-secondaryGON-50 text-secondaryGON-400 rounded justify-start items-start flex'>
                      {totalStock} Pcs
                    </div>
                    <Link
                      aria-label={`${val?.name_product}`}
                      as={`/products/${val?.name_product
                        ?.replaceAll("&", "and")
                        ?.replaceAll(" ", "-")}-${val?.product_id}`}
                      shallow
                      passHref
                      target='_blank'
                      rel='preload'
                      href={`/products/${val?.name_product
                        ?.replaceAll("&", "and")
                        ?.replaceAll(" ", "-")}-${val?.product_id}`}
                      className='flex justify-end items-center'>
                      <div className='flex items-center gap-1 overflow-hidden'>
                        <span
                          className={cn(
                            "whitespace-nowrap truncate text-xs font-semibold text-primaryGON-400"
                          )}>
                          Beli Sekarang
                        </span>
                      </div>
                    </Link>
                  </div>
                </div>
                <Divider />
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-[50px] text-left xs:p3-regular text-xs font-poppins'>
                        Color
                      </TableHead>
                      <TableHead className='w-[50px] text-left xs:p3-regular text-xs font-poppins'>
                        Size
                      </TableHead>
                      <TableHead className='w-[50px] text-left xs:p3-regular text-xs font-poppins'>
                        Stok
                      </TableHead>
                      <TableHead className='w-[50px] text-right xs:p3-regular text-xs font-poppins'>
                        {breakpoint == "ui-phone" ? "Disc" : "Diskon"}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className='border border-greyscaleGON-200'>
                    {Object.keys(val.variants).map((color, index) => (
                      <React.Fragment key={color}>
                        {val.variants[color].map((variant, varIndex) => (
                          <TableRow
                            key={variant.variant_id}
                            className='items-start justify-start border border-greyscaleGON-200'>
                            <TableCell className='w-[50px] text-left xs:p3-regular text-xs font-poppins '>
                              {color}
                            </TableCell>
                            <TableCell className='w-[50px] text-left xs:p3-regular text-xs font-poppins'>
                              {variant.variant1}
                            </TableCell>
                            <TableCell className='w-[50px] text-left xs:p3-regular text-xs font-poppins'>
                              {variant.stock}
                            </TableCell>
                            <TableCell className='w-[50px] text-right xs:p3-regular text-xs font-poppins'>
                              {variant.diskon_product}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })
        )}
      </>
    </div>
  );
};
